// 🔧 communication_studio.js
// ✅ Purpose: Admin Communication Studio MVP for editing LGV platform copy
// ✅ Features:
// - Loads editable Communication Engine content items by area
// - Supports starter content rows when an area has not been seeded yet
// - Allows admins to edit values in a simple Bootstrap card UI
// - Saves all visible rows as drafts
// - Publishes all visible rows and rebuilds the runtime content bundle
// - Shows success/error messages at the top of the page for 20 seconds
// - Uses the Communication Engine service layer instead of querying Firestore directly
// - Keeps UI intentionally simple for the first vertical slice

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import { auth } from "../../firebase";

import {
  buildContentKey,
} from "../../content_engine";

import {
  createContentItem,
} from "../../content_engine/models/content_item_model";

import {
  getContentItems,
  publishArea,
  saveDraft,
} from "../../content_engine/engine/content_service";

import {
  CONTENT_STATUSES,
} from "../../content_engine/constants/content_statuses";

import {
  CONTENT_TYPES,
} from "../../content_engine/constants/content_types";

import {
  DEFAULT_SCOPE_ID,
} from "../../content_engine/constants/content_scopes";

import {
  DEFAULT_CONTENT_LOCALE,
} from "../../content_engine/constants/content_locales";

import {
  DEFAULT_CONTENT_PROFILE,
} from "../../content_engine/constants/content_profiles";

import {
    EMAIL_TEMPLATES,
    buildEmailStarterItems,
} from "../../content_engine/constants/email_templates";

/**
 * Starter areas for the MVP Studio.
 *
 * These are intentionally small.
 * More areas can be added as pages are migrated.
 */
const CONTENT_AREAS = [
  {
    area: "event_home",
    label: "Event Home",
    description: "Public-facing event landing page content.",
  },
  {
    area: "dashboard",
    label: "Dashboard",
    description: "Signed-in user dashboard content.",
  },
  {
    area: "request_event",
    label: "Request Event",
    description: "Public event request form content.",
  },
  {
    area: "help",
    label: "Help / FAQ",
    description: "Public help and support content.",
  },
  //
  // ============================================================
  // 📧 Email Templates
  // ============================================================
  //

  ...EMAIL_TEMPLATES.map((template) => ({

      area: `email_${template.id}`,

      label: `📧 ${template.label}`,

      description: template.description,

  })),

];

/**
 * MVP starter rows.
 *
 * These are used only when Firestore has no content items
 * for the selected area yet.
 */

function buildEmailTemplateStarterRows({
  subject = "",
  heading = "",
  html = "",
  text = "",
  buttonLabel = "",
  footer = "This email was generated automatically by Let's Go Vote.",
} = {}) {
  return [
    {
      section: "email",
      element: "subject",
      label: "Email Subject",
      type: CONTENT_TYPES.EMAIL_SUBJECT,
      defaultValue: subject,
    },
    {
      section: "email",
      element: "heading",
      label: "Email Heading",
      type: CONTENT_TYPES.EMAIL_HEADING,
      defaultValue: heading,
    },
    {
      section: "email",
      element: "html",
      label: "HTML Email Body",
      type: CONTENT_TYPES.RICH_TEXT,
      defaultValue: html,
    },
    {
      section: "email",
      element: "text",
      label: "Plain Text Email Body",
      type: CONTENT_TYPES.EMAIL_BODY,
      defaultValue: text,
    },
    {
      section: "email",
      element: "button_label",
      label: "Button Label",
      type: CONTENT_TYPES.EMAIL_BUTTON_LABEL,
      defaultValue: buttonLabel,
    },
    {
      section: "email",
      element: "footer",
      label: "Email Footer",
      type: CONTENT_TYPES.EMAIL_FOOTER,
      defaultValue: footer,
    },
  ];
}

const STARTER_ITEMS_BY_AREA = {
  //
  // ============================================================
  // 📧 Build starter rows from Email Registry
  // ============================================================
  //

  ...Object.fromEntries(

      EMAIL_TEMPLATES.map((template) => [

          `email_${template.id}`,

          buildEmailStarterItems(template),

      ])

  ),
  event_home: [
    {
      section: "hero",
      element: "title",
      label: "Hero Title",
      type: CONTENT_TYPES.HEADING,
      defaultValue: "Welcome to the Event",
    },
    {
      section: "hero",
      element: "subtitle",
      label: "Hero Subtitle",
      type: CONTENT_TYPES.BODY_TEXT,
      defaultValue: "Explore the event, vote, and follow the latest updates.",
    },
    {
      section: "vote",
      element: "button",
      label: "Vote Button",
      type: CONTENT_TYPES.BUTTON_LABEL,
      defaultValue: "Vote Now",
    },
    {
      section: "sponsors",
      element: "heading",
      label: "Sponsors Heading",
      type: CONTENT_TYPES.HEADING,
      defaultValue: "Event Sponsors",
    },
    {
      section: "stay_connected",
      element: "heading",
      label: "Stay Connected Heading",
      type: CONTENT_TYPES.HEADING,
      defaultValue: "Stay Connected",
    },    
   {
      section: "stay_connected",
      element: "description",
      label: "Stay Connected Description",
      type: CONTENT_TYPES.BODY_TEXT,
      defaultValue:
          "Keep connected by providing your email. We will keep you up to date with future event information.",
    },   
    {
      section: "stay_connected",
      element: "saving_button",
      label: "Stay Connected Saving Button",
      type: CONTENT_TYPES.BUTTON_LABEL,
      defaultValue: "Saving...",
    },
    {
      section: "stay_connected",
      element: "join_button",
      label: "Stay Connected Join Button",
      type: CONTENT_TYPES.BUTTON_LABEL,
      defaultValue: "Join",
    },   
    {
      section: "stay_connected",
      element: "share_heading",
      label: "Share Heading",
      type: CONTENT_TYPES.HEADING,
      defaultValue: "Share this event",
    },    
    {
      section: "stay_connected",
      element: "email_placeholder",
      label: "Stay Connected Email Placeholder",
      type: CONTENT_TYPES.BODY_TEXT,
      defaultValue: "Enter your email",
    },    
    {
      section: "stay_connected",
      element: "success_message",
      label: "Stay Connected Success Message",
      type: CONTENT_TYPES.BODY_TEXT,
      defaultValue: "Thanks for joining our mailing list!",
    },    
  ],

  dashboard: [
    {
      section: "hero",
      element: "title",
      label: "Dashboard Title",
      type: CONTENT_TYPES.HEADING,
      defaultValue: "Dashboard",
    },
    {
      section: "events",
      element: "heading",
      label: "Events Heading",
      type: CONTENT_TYPES.HEADING,
      defaultValue: "Your Events",
    },
  ],

  request_event: [
    {
      section: "hero",
      element: "title",
      label: "Request Event Title",
      type: CONTENT_TYPES.HEADING,
      defaultValue: "Request an Event",
    },
    {
      section: "form",
      element: "submit_button",
      label: "Submit Button",
      type: CONTENT_TYPES.BUTTON_LABEL,
      defaultValue: "Submit Request",
    },
  ],

  help: [
    {
      section: "hero",
      element: "title",
      label: "Help Title",
      type: CONTENT_TYPES.HEADING,
      defaultValue: "Help & Frequently Asked Questions",
    },
    {
      section: "hero",
      element: "subtitle",
      label: "Help Subtitle",
      type: CONTENT_TYPES.BODY_TEXT,
      defaultValue: "Find answers and guidance for using Let’s Go Vote.",
    },
  ],

  email_event_approval: buildEmailTemplateStarterRows({
    subject: "✅ Your Let's Go Vote event is ready: {eventName}",
    heading: "✅ Event Ready",
    html:
      "<p>Hi {recipientName},</p><p>Your event <strong>{eventName}</strong> is now live in Let's Go Vote.</p><p><a href=\"{eventUrl}\">{buttonLabel}</a></p>",
    text:
      "Hi {recipientName},\n\nYour event {eventName} is now live in Let's Go Vote.\n\nOpen your event:\n{eventUrl}",
    buttonLabel: "Open Event",
  }),

  email_event_request: buildEmailTemplateStarterRows({
    subject: "🚀 New Event Request: {eventName}",
    heading: "🚀 New Event Request",
    html:
      "<p>A new Let's Go Vote event request has been submitted.</p><p><strong>Event:</strong> {eventName}</p><p><strong>Contact:</strong> {contactEmail}</p>",
    text:
      "New Let's Go Vote event request submitted.\n\nEvent: {eventName}\nContact: {contactEmail}",
    buttonLabel: "Review Request",
  }),

  email_user_invite: buildEmailTemplateStarterRows({
    subject: "📨 Invitation to {eventName}",
    heading: "You're Invited",
    html:
      "<p>Hello {recipientName},</p><p>You've been invited to participate in <strong>{eventName}</strong>.</p><p><a href=\"{inviteUrl}\">{buttonLabel}</a></p>",
    text:
      "Hello {recipientName},\n\nYou've been invited to participate in {eventName}.\n\nRegister here:\n{inviteUrl}",
    buttonLabel: "Complete Registration",
  }),

  email_sponsor_contact: buildEmailTemplateStarterRows({
    subject: "🌟 You've been added as a sponsor contact for {eventName}",
    heading: "Sponsor Contact Added",
    html:
      "<p>Hello {recipientName},</p><p>You have been listed as the sponsor contact for <strong>{sponsorName}</strong>.</p><p><a href=\"{sponsorUrl}\">{buttonLabel}</a></p>",
    text:
      "Hello {recipientName},\n\nYou have been listed as the sponsor contact for {sponsorName}.\n\nReview sponsor information:\n{sponsorUrl}",
    buttonLabel: "Review Sponsor",
  }),

  email_volunteer_invite: buildEmailTemplateStarterRows({
    subject: "You're Signed Up to Volunteer: {activityTitle}",
    heading: "Volunteer Signup",
    html:
      "<p>Hi {recipientName},</p><p>Thank you for signing up to volunteer for <strong>{activityTitle}</strong>.</p><p>Your shift is: {shiftTime}</p>",
    text:
      "Hi {recipientName},\n\nThank you for signing up to volunteer for {activityTitle}.\n\nYour shift is: {shiftTime}",
    buttonLabel: "View Volunteer Details",
  }),

  email_volunteer_signup: buildEmailTemplateStarterRows({
    subject: "✅ Confirmed: {activityTitle} - {eventName}",
    heading: "Volunteer Confirmed",
    html:
      "<p>Hi {recipientName},</p><p>You're confirmed to volunteer for <strong>{activityTitle}</strong> at <strong>{eventName}</strong>.</p><p>Your shift is: {shiftTime}</p>",
    text:
      "Hi {recipientName},\n\nYou're confirmed to volunteer for {activityTitle} at {eventName}.\n\nYour shift is: {shiftTime}",
    buttonLabel: "View Duties",
  }),

  email_auction_outbid: buildEmailTemplateStarterRows({
    subject: "You've Been Outbid - {itemTitle}",
    heading: "You've Been Outbid",
    html:
      "<p>Hi {recipientName},</p><p>You have been <strong>outbid</strong> on {itemTitle}.</p><p>Current bid: ${currentBid}</p><p><a href=\"{auctionUrl}\">{buttonLabel}</a></p>",
    text:
      "Hi {recipientName},\n\nYou have been outbid on {itemTitle}.\n\nCurrent bid: ${currentBid}\n\nPlace another bid:\n{auctionUrl}",
    buttonLabel: "Place Another Bid",
  }),

};

/**
 * Builds starter content items for an area.
 */
function buildStarterItems(area) {
  const starterRows =
    STARTER_ITEMS_BY_AREA[area] || [];

  return starterRows.map((row) => {
    const key =
      buildContentKey({
        area,
        section: row.section,
        element: row.element,
      });

    return createContentItem({
      key,
      area,
      section: row.section,
      element: row.element,
      type: row.type,
      value: row.defaultValue,
      defaultValue: row.defaultValue,
      status: CONTENT_STATUSES.DRAFT,
      scopeId: DEFAULT_SCOPE_ID,
      locale: DEFAULT_CONTENT_LOCALE,
      profile: DEFAULT_CONTENT_PROFILE,
      tags: [area, row.section],
      consumerEngines: ["react_page"],
    });
  });
}

export default function CommunicationStudio() {
  const [selectedArea, setSelectedArea] =
    useState("event_home");

  const [items, setItems] =
    useState([]);

  const [searchText, setSearchText] = useState("");

  const [loading, setLoading] =
    useState(true);

  const [saving, setSaving] =
    useState(false);

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const [message, setMessage] =
    useState(null);

  const selectedAreaMeta =
    useMemo(
      () =>
        CONTENT_AREAS.find(
          (areaConfig) =>
            areaConfig.area === selectedArea
        ),
      [selectedArea]
    );

  /**
   * Shows a colored message for 20 seconds.
   */
  function showMessage(type, text) {
    setMessage({
      type,
      text,
    });

    window.setTimeout(() => {
      setMessage(null);
    }, 20000);
  }

  /**
   * Loads editable items for the selected area.
   */
  async function loadItems() {
    try {
      setLoading(true);

      const existingItems =
        await getContentItems({
          area: selectedArea,
          scopeId: DEFAULT_SCOPE_ID,
          locale: DEFAULT_CONTENT_LOCALE,
          profile: DEFAULT_CONTENT_PROFILE,
        });

      if (existingItems.length > 0) {
        setItems(existingItems);
        setHasUnsavedChanges(false);
      } else {
        setItems(
          buildStarterItems(selectedArea)
        );
        setHasUnsavedChanges(false);
      }
    } catch (error) {
      console.error(
        "[Communication Studio] Failed to load content items.",
        error
      );

      showMessage(
        "danger",
        "Unable to load content items."
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedArea]);

  /**
   * Updates one field in local state.
   */
  function updateItemValue(key, value) {
    setItems((currentItems) =>
      currentItems.map((item) =>
        item.key === key
          ? {
              ...item,
              value,
              updatedBy:
                auth.currentUser?.uid || null,
            }
          : item
      )
    );
    setHasUnsavedChanges(true);
  }

  /**
   * Saves all current rows as drafts.
   */
  async function handleSaveDrafts() {
    try {
      setSaving(true);

        await Promise.all(
          items.map((item) =>
            saveDraft({
              ...item,
              status: CONTENT_STATUSES.DRAFT,
              updatedBy:
                auth.currentUser?.uid || null,
            })
          )
        );

      showMessage(
        "success",
        "Draft content saved."
      );
      setHasUnsavedChanges(false);

      await loadItems();
    } catch (error) {
      console.error(
        "[Communication Studio] Failed to save drafts.",
        error
      );

      showMessage(
        "danger",
        "Unable to save draft content."
      );
    } finally {
      setSaving(false);
    }
  }

  /**
   * Publishes all current rows and rebuilds the bundle.
   */
  async function handlePublishArea() {
    try {
      setSaving(true);

      await Promise.all(
        items.map((item) =>
          saveDraft({
            ...item,
            status: CONTENT_STATUSES.PUBLISHED,
            publishedBy:
              auth.currentUser?.uid || null,
            updatedBy:
              auth.currentUser?.uid || null,
          })
        )
      );

      await publishArea({
        area: selectedArea,
        scopeId: DEFAULT_SCOPE_ID,
        locale: DEFAULT_CONTENT_LOCALE,
        profile: DEFAULT_CONTENT_PROFILE,
        publishedBy:
          auth.currentUser?.uid || null,
      });

      showMessage(
        "success",
        "Content published and bundle rebuilt."
      );
      setHasUnsavedChanges(false);

      await loadItems();
    } catch (error) {
      console.error(
        "[Communication Studio] Failed to publish area.",
        error
      );

      showMessage(
        "danger",
        "Unable to publish content."
      );
    } finally {
      setSaving(false);
    }
  }

  const filteredItems = useMemo(() => {
    if (!searchText.trim()) {
      return items;
    }

    const search = searchText.toLowerCase();

    return items.filter((item) =>
      [
        item.section,
        item.element,
        item.key,
        item.value,
        item.defaultValue,
      ]
        .join(" ")
        .toLowerCase()
        .includes(search)
    );
  }, [items, searchText]);

  const groupedItems = useMemo(() => {
    return filteredItems.reduce((groups, item) => {
      const sectionName = item.section || "general";

      if (!groups[sectionName]) {
        groups[sectionName] = [];
      }

      groups[sectionName].push(item);

      return groups;
    }, {});
  }, [filteredItems]);

  return (
    <div className="container-fluid py-4">
      <div
        className="container"
        style={{
          maxWidth: "1200px",
        }}
      >
        {message && (
          <div
            className={`alert alert-${message.type}`}
            role="alert"
          >
            {message.text}
          </div>
        )}

        <div className="card shadow-sm mb-4">
          <div className="card-body">
            <h1 className="h3 mb-2">
              Communication Studio
            </h1>

            <p className="text-muted mb-0">
              Manage editable platform copy, publish runtime bundles,
              and prepare LGV for localization, accessibility, AI, and
              white-label communication.
            </p>
          </div>
        </div>

        <div className="row g-4">
          <div className="col-12 col-lg-4">
            <div className="card shadow-sm">
              <div className="card-header bg-white">
                <strong>Content Areas</strong>
              </div>

              <div className="list-group list-group-flush">
                {CONTENT_AREAS.map((areaConfig) => (
                  <button
                    key={areaConfig.area}
                    type="button"
                    className={
                      areaConfig.area === selectedArea
                        ? "list-group-item list-group-item-action active"
                        : "list-group-item list-group-item-action"
                    }
                    onClick={() =>
                      setSelectedArea(areaConfig.area)
                    }
                  >
                    <div className="fw-semibold">
                      {areaConfig.label}
                    </div>

                    <div className="small">
                      {areaConfig.description}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-8">
            <div className="card shadow-sm">
              <div className="card-header bg-white d-flex flex-column flex-md-row justify-content-between gap-2">
                <div>
                  <strong>
                    {selectedAreaMeta?.label ||
                      selectedArea}
                  </strong>

                  <div className="small text-muted">
                    Area key: {selectedArea}
                  </div>
                  
                  <div className="small mt-1">
                    {hasUnsavedChanges ? (
                      <span className="badge bg-warning text-dark">
                        ● Unsaved Changes
                      </span>
                    ) : (
                      <span className="badge bg-success">
                        ✓ Saved
                      </span>
                    )}
                  </div>

                  <div className="small text-muted">
                    {filteredItems.length} content items
                  </div>
                </div>

                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={handleSaveDrafts}
                    disabled={saving || loading}
                  >
                    Save Drafts
                  </button>

                  <button
                    type="button"
                    className="btn btn-success btn-sm"
                    onClick={handlePublishArea}
                    disabled={saving || loading}
                  >
                    Publish Area
                  </button>
                </div>
              </div>

              <div className="card-body">
                <div className="mb-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search content..."
                    value={searchText}
                    onChange={(event) =>
                      setSearchText(event.target.value)
                    }
                  />
                </div>

                {loading ? (
                  <p className="text-muted mb-0">
                    Loading content items...
                  </p>
                ) : items.length === 0 ? (
                  <p className="text-muted mb-0">
                    No content items found for this area.
                  </p>
                ) : (

                <div className="d-flex flex-column gap-4">
                  {filteredItems.length === 0 ? (
                    <div className="alert alert-info mb-0">
                      No content items match your search.
                    </div>
                  ) : (
                    Object.entries(groupedItems).map(([sectionName, sectionItems]) => (
                      <div key={sectionName} className="card border shadow-sm">
                        <div className="card-header bg-white">
                          <strong className="text-capitalize">
                            {sectionName.replaceAll("_", " ")}
                          </strong>

                          <div className="small text-muted">
                            {sectionItems.length} item{sectionItems.length === 1 ? "" : "s"}
                          </div>
                        </div>

                        <div className="card-body d-flex flex-column gap-3">
                          {sectionItems.map((item) => (
                            <div
                              key={item.key}
                              className="border rounded p-3 bg-light"
                            >
                              <div className="d-flex flex-column flex-md-row justify-content-between gap-2 mb-2">
                                <div>
                                  <label
                                    className="form-label fw-semibold mb-1"
                                    htmlFor={item.key}
                                  >
                                    {item.label || `${item.section}.${item.element}`}
                                  </label>

                                  <div className="small text-muted">
                                    {item.key}
                                  </div>
                                </div>

                                <div className="d-flex flex-wrap gap-2">
                                  <span className="badge bg-secondary">
                                    {item.status}
                                  </span>

                                  {item.type && (
                                    <span className="badge bg-light text-dark border">
                                      {item.type}
                                    </span>
                                  )}
                                </div>
                              </div>

                              <textarea
                                id={item.key}
                                data-testid={`content-${item.key}`}
                                className="form-control"
                                rows={3}
                                value={item.value || ""}
                                onChange={(event) =>
                                  updateItemValue(
                                    item.key,
                                    event.target.value
                                  )
                                }
                              />

                              <div className="d-flex flex-column flex-md-row justify-content-between gap-2 mt-2">
                                <div className="small text-muted">
                                  Default:{" "}
                                  {item.defaultValue ||
                                    "No default value set."}
                                </div>

                                <div className="small text-muted">
                                  {(item.value || "").length} characters
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </div>

                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}